//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { getAnalyticsProviderSettings } from '@account-settings/redux/analyticsProviders/analyticsProvidersSlice';
import { getImpactConnection } from '@apis/account-settings/impactAccountApi';
import {
  connectAdobeAnalyticsOAuth,
  deauthGAAccount,
  deleteGA4BigQueryDataset,
  getAdobeCompanies,
  getAdobeOAuthSettings,
  getCuralatePixelSettings,
  getGA4BigQueryDataset,
  getGA4Settings,
  getGAOAuthURL,
  subscribeAnalyticsHubListing,
  updateAdobeGlobalCompanyId,
  updateGAProfile,
} from '@apis/analytics-settings/analyticsSettingsApi';
import {
  IAdobeAuthenticationCredentials,
  IGetAdobeSettingsResponse,
  IGetCuralatePixelSettingsResponse,
} from '@apis/analytics-settings/analyticsSettingsApiModel';
import { getConnectedFacebookPageToken } from '@apis/facebook-settings/gatewayFacebookSettingsApi';
import {
  getConnectedInstagramAccount,
  getConnectedInstagramAccountsMessagesAccessStatus,
} from '@apis/instagram-settings/gatewayInstagramSettingsApi';
import {
  GetConnectedInstagramAccountsMessagesAccessStatusResponse,
  IGetConnectedInstagramAccountResponse,
} from '@apis/instagram-settings/gatewayInstagramSettingsApiModel';
import { getUser } from '@apis/pinterest-user/pinterestUserApi';
import { getOverviewItems } from '@apis/product-feed-health/productFeedHealthApi';
import { IProductFeedHealthOverviewResponse } from '@apis/product-feed-health/productFeedHealthApiModels';
import { getTikTokUser } from '@apis/tiktok-user/tiktokUserApi';
import { getTwitterSettings } from '@apis/twitter-settings/twitterSettingsApi';
import { getUser as getTwitterUser } from '@apis/twitter-user/twitterUserApi';
import { IGetUserRequest } from '@apis/twitter-user/twitterUserApiModel';
import { getUserSettings } from '@apis/user-settings/userSettingsApi';
import { getYouTubeUser } from '@apis/youtube/youtubeApi';
import { YouTubeUser } from '@apis/youtube/youtubeApiModel';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import GaProfileId from '@models/GaProfileId';
import { IUserSettings } from '@models/IUserSettings';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { recordEvent } from '@tracker/tracker';
import {
  TRACKING_ACTION,
  TRACKING_CATEGORY,
  TRACKING_LABEL,
} from '@tracker/trackerModel';
import { isTruthy } from '@utils/filterPredicates';
import { RequestError } from '@utils/sessionFetch';

export const useConnectedInstagramAccountQuery = <
  TData = IGetConnectedInstagramAccountResponse,
>(
  select?: (data: IGetConnectedInstagramAccountResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsSocial,
      QueryKeyConstants.AccountSettingsSocialInstagramAccount,
    ],
    queryFn: async ({ signal }) => getConnectedInstagramAccount({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useCurrentUserSettingsQuery = <TData = IUserSettings>(
  select?: (data: IUserSettings) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsCurrentUser,
    ],
    queryFn: async ({ signal }) => getUserSettings({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useProductFeedHealthOverviewQuery = <
  TData = IProductFeedHealthOverviewResponse,
>(
  select?: (data: IProductFeedHealthOverviewResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsProductFeedHealthOverview,
    ],
    queryFn: async ({ signal }) => getOverviewItems({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const googleAnalytics4SettingsQueryOptions = queryOptions({
  queryKey: [
    QueryKeyConstants.AccountSettings,
    QueryKeyConstants.AccountSettingsAnalytics,
    QueryKeyConstants.AccountSettingsAnalyticsGoogle,
    QueryKeyConstants.AccountSettingsAnalyticsGoogleAnalytics4,
  ],
  queryFn: async ({ signal }) => getGA4Settings({ signal }),
  staleTime: FIVE_MINUTES_STALE_TIME,
});

export const googleAnalytics4BigQueryDatasetQueryOptions = queryOptions({
  queryKey: [
    QueryKeyConstants.AccountSettings,
    QueryKeyConstants.AccountSettingsAnalytics,
    QueryKeyConstants.AccountSettingsAnalyticsGoogle,
    QueryKeyConstants.AccountSettingsAnalyticsGoogleBigQuery,
  ],
  queryFn: async ({ signal }) => {
    const dataset = await getGA4BigQueryDataset({ signal });
    return dataset ?? null;
  },
  staleTime: FIVE_MINUTES_STALE_TIME,
});

export const useConnectGoogleAnalyticsMutation = () => {
  const dispatch = useDispatch();

  return useMutation({
    mutationKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsAnalytics,
      QueryKeyConstants.AccountSettingsAnalyticsGoogle,
      QueryKeyConstants.AccountSettingsAnalyticsGoogleConnect,
    ],
    mutationFn: async () => {
      const response = await getGAOAuthURL();
      if (!response.url) {
        throw new Error('Google Analytics did not return an OAuth URL');
      }

      return response.url;
    },
    onSuccess: (oauthUrl) => {
      recordEvent(
        TRACKING_CATEGORY.SETTINGS,
        TRACKING_ACTION.CONNECT_ACCOUNT,
        TRACKING_LABEL.SUCCESS,
      );
      window.location.href = oauthUrl;
    },
    onError: () => {
      recordEvent(
        TRACKING_CATEGORY.SETTINGS,
        TRACKING_ACTION.CONNECT_ACCOUNT,
        TRACKING_LABEL.FAIL,
      );
      dispatch(
        toastNotificationsActions.addToastNotification({
          title:
            'An error occurred when trying to connect your Google Analytics account.',
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          type: ToastType.ERROR,
        }),
      );
    },
  });
};

export const useDisconnectGoogleAnalyticsMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsAnalytics,
      QueryKeyConstants.AccountSettingsAnalyticsGoogle,
      QueryKeyConstants.AccountSettingsAnalyticsGoogleDisconnect,
    ],
    mutationFn: async (shouldDisconnectBigQuery: boolean) => {
      await deauthGAAccount();

      if (!shouldDisconnectBigQuery) {
        return false;
      }

      try {
        await deleteGA4BigQueryDataset();
        return false;
      } catch (error) {
        if (error instanceof RequestError && error.statusCode === 404) {
          return false;
        }

        return true;
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.AccountSettings,
          QueryKeyConstants.AccountSettingsAnalytics,
          QueryKeyConstants.AccountSettingsAnalyticsGoogle,
        ],
      });
    },
    onSuccess: (didBigQueryDisconnectFail) => {
      recordEvent(
        TRACKING_CATEGORY.SETTINGS,
        TRACKING_ACTION.DISCONNECT_ACCOUNT,
        TRACKING_LABEL.SUCCESS,
      );
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Google Analytics account successfully disconnected!',
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          type: ToastType.SUCCESS,
        }),
      );
      if (didBigQueryDisconnectFail) {
        dispatch(
          toastNotificationsActions.addToastNotification({
            title:
              'An error occurred when trying to disconnect your Google Analytics BigQuery dataset.',
            options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
            type: ToastType.ERROR,
          }),
        );
      }
    },
    onError: () => {
      recordEvent(
        TRACKING_CATEGORY.SETTINGS,
        TRACKING_ACTION.DISCONNECT_ACCOUNT,
        TRACKING_LABEL.FAIL,
      );
      dispatch(
        toastNotificationsActions.addToastNotification({
          title:
            'An error occurred when trying to disconnect your Google Analytics account.',
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          type: ToastType.ERROR,
        }),
      );
    },
  });
};

export const useUpdateGoogleAnalyticsProfileMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsAnalytics,
      QueryKeyConstants.AccountSettingsAnalyticsGoogle,
      QueryKeyConstants.AccountSettingsAnalyticsGoogleUpdateProfile,
    ],
    mutationFn: (profileId: GaProfileId) =>
      updateGAProfile(encodeURIComponent(profileId)),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.AccountSettings,
          QueryKeyConstants.AccountSettingsAnalytics,
          QueryKeyConstants.AccountSettingsAnalyticsGoogle,
        ],
      });
    },
    onSuccess: () => {
      recordEvent(
        TRACKING_CATEGORY.SETTINGS,
        TRACKING_ACTION.GA_SWITCH_PROFILE,
        TRACKING_LABEL.SUCCESS,
      );
    },
    onError: () => {
      recordEvent(
        TRACKING_CATEGORY.SETTINGS,
        TRACKING_ACTION.GA_SWITCH_PROFILE,
        TRACKING_LABEL.FAIL,
      );
      dispatch(
        toastNotificationsActions.addToastNotification({
          title:
            'An error occurred when trying to update your Google Analytics profile.',
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          type: ToastType.ERROR,
        }),
      );
    },
  });
};

export const useDeleteGoogleAnalyticsBigQueryDatasetMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsAnalytics,
      QueryKeyConstants.AccountSettingsAnalyticsGoogle,
      QueryKeyConstants.AccountSettingsAnalyticsGoogleDeleteBigQueryDataset,
    ],
    mutationFn: async () => {
      try {
        await deleteGA4BigQueryDataset();
      } catch (error) {
        if (error instanceof RequestError && error.statusCode === 404) {
          return;
        }

        throw error;
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.AccountSettings,
          QueryKeyConstants.AccountSettingsAnalytics,
          QueryKeyConstants.AccountSettingsAnalyticsGoogle,
        ],
      });
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title:
            'An error occurred when trying to disconnect your Google Analytics BigQuery dataset.',
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          type: ToastType.ERROR,
        }),
      );
    },
  });
};

export const useSubscribeGoogleAnalyticsHubListingMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsAnalytics,
      QueryKeyConstants.AccountSettingsAnalyticsGoogle,
      QueryKeyConstants.AccountSettingsAnalyticsGoogleSubscribeAnalyticsHubListing,
    ],
    mutationFn: (url: string) => subscribeAnalyticsHubListing(url),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.AccountSettings,
          QueryKeyConstants.AccountSettingsAnalytics,
          QueryKeyConstants.AccountSettingsAnalyticsGoogle,
        ],
      });
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title:
            'An error occurred when trying to subscribe to your Google Analytics Hub listing.',
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          type: ToastType.ERROR,
        }),
      );
    },
  });
};

export const useAdobeOAuthSettingsQuery = <TData = IGetAdobeSettingsResponse>(
  select?: (data: IGetAdobeSettingsResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsAnalytics,
      QueryKeyConstants.AccountSettingsAnalyticsAdobe,
      QueryKeyConstants.AccountSettingsAnalyticsAdobeOAuth,
    ],
    queryFn: async ({ signal }) => getAdobeOAuthSettings({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useConnectAdobeOAuthMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: IAdobeAuthenticationCredentials) =>
      connectAdobeAnalyticsOAuth(credentials),
    onSuccess: () => {
      recordEvent(
        TRACKING_CATEGORY.SETTINGS,
        TRACKING_ACTION.CONNECT_ACCOUNT,
        TRACKING_LABEL.SUCCESS,
      );
      void dispatch(getAnalyticsProviderSettings());
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.AccountSettings,
          QueryKeyConstants.AccountSettingsAnalytics,
          QueryKeyConstants.AccountSettingsAnalyticsAdobe,
          QueryKeyConstants.AccountSettingsAnalyticsAdobeOAuth,
        ],
      });
    },
    onError: () => {
      recordEvent(
        TRACKING_CATEGORY.SETTINGS,
        TRACKING_ACTION.CONNECT_ACCOUNT,
        TRACKING_LABEL.FAIL,
      );
    },
  });
};

export const useAdobeCompaniesQuery = (enabled: boolean) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsAnalytics,
      QueryKeyConstants.AccountSettingsAnalyticsAdobe,
      QueryKeyConstants.AccountSettingsAnalyticsAdobeCompanies,
    ],
    queryFn: async ({ signal }) => getAdobeCompanies({ signal }),
    enabled,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useUpdateAdobeGlobalCompanyIdMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (globalCompanyId: string) =>
      updateAdobeGlobalCompanyId(globalCompanyId),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.AccountSettings,
          QueryKeyConstants.AccountSettingsAnalytics,
          QueryKeyConstants.AccountSettingsAnalyticsAdobe,
        ],
      });
    },
  });
};

export const useVibePixelSettingsQuery = <
  TData = IGetCuralatePixelSettingsResponse,
>(
  select?: (data: IGetCuralatePixelSettingsResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsAnalytics,
      QueryKeyConstants.AccountSettingsAnalyticsVibePixel,
    ],
    queryFn: async ({ signal }) => getCuralatePixelSettings({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useConnectedInstagramAccountsMessagesAccessStatusQuery = <
  TData = GetConnectedInstagramAccountsMessagesAccessStatusResponse,
>(
  select?: (
    data: GetConnectedInstagramAccountsMessagesAccessStatusResponse,
  ) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsSocial,
      QueryKeyConstants.AccountSettingsSocialInstagramAccount,
      QueryKeyConstants.AccountSettingsSocialInstagramAccountMessagesAccessStatus,
    ],
    queryFn: async ({ signal }) =>
      getConnectedInstagramAccountsMessagesAccessStatus({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useConnectedFacebookPageTokenQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsSocial,
      QueryKeyConstants.AccountSettingsSocialFacebook,
    ],
    queryFn: () => getConnectedFacebookPageToken(),
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export function usePinterestUserQuery(isPinterestConnected: boolean) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsSocial,
      QueryKeyConstants.AccountSettingsSocialPinterestUser,
    ],
    queryFn: async ({ signal }) => {
      const user = await getUser({ signal });
      return user ?? null;
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isPinterestConnected,
  });
}

export function useGetYouTubeUserQuery<TData = YouTubeUser>(
  select?: (data: YouTubeUser) => TData,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsSocial,
      QueryKeyConstants.AccountSettingsSocialYouTubeUser,
    ],
    queryFn: async ({ signal }) => {
      return getYouTubeUser({ signal });
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    select,
  });
}

export const useTikTokUserQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsSocial,
      QueryKeyConstants.AccountSettingsSocialTikTokUser,
    ],
    queryFn: async ({ signal }) => {
      return getTikTokUser({ signal });
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useTwitterSettingsQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsSocial,
      QueryKeyConstants.AccountSettingsSocialTwitter,
      QueryKeyConstants.AccountSettingsSocialTwitterSettings,
    ],
    queryFn: async ({ signal }) => {
      return getTwitterSettings({ signal });
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useTwitterUserQuery = (twitterUsername: string | undefined) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsSocial,
      QueryKeyConstants.AccountSettingsSocialTwitter,
      QueryKeyConstants.AccountSettingsSocialTwitterUser,
      twitterUsername,
    ],
    queryFn: async ({ signal }) => {
      if (!twitterUsername) {
        throw new Error('Twitter username is required');
      }
      const req: IGetUserRequest = { twitterUsername };
      const user = await getTwitterUser(req, { signal });

      return user ?? null;
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isTruthy(twitterUsername),
  });
};

export const useImpactConnectionQuery = ({
  enabled,
}: { enabled?: boolean } = {}) => {
  return useQuery({
    enabled,
    queryFn: async ({ signal }) => getImpactConnection({ signal }),
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsImpact,
      QueryKeyConstants.AccountSettingsImpactConnection,
    ],
    retry: false,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};
